/**
 * SearchGridController - Handles search-specific product grid functionality
 * Extends ProductGridManager with search-specific features.
 *
 * Search matching runs entirely client-side against the prebuilt index
 * (search-index.json), using the shared, unit-tested logic in
 * assets/search-matching.js. The index carries every field the grid renders, so
 * there are no per-search API round trips: load the index once, then match,
 * filter, sort, and paginate locally. Filter/sort/pagination changes recompute
 * instantly from the in-memory index.
 */

class SearchGridController extends ProductGridManager {
  constructor(options = {}) {
    super(options);
    // Strip any legacy "title:" prefix (from old bookmarked URLs) so it isn't
    // matched literally against titles.
    this.searchTerms = (options.searchTerms || '').replace(/^title:/, '');
    this.searchResultsCount = options.searchResultsCount || 0;
    this.isSearchMode = true;
    this.indexRows = null;

    // Memoize the computed result set keyed by (query + filters) so paging
    // through results doesn't re-run the match/filter/sort each click.
    this._resultsKey = null;
    this._results = [];
  }

  async initialize() {
    try {
      this.updateCacheStatus('Loading search results...');
      this.restoreStateFromURL(false);
      this.populateFilterOptions();

      if (!window.SearchIndexLoader || !window.SearchMatching) {
        throw new Error('Search index loader / matcher not available');
      }

      this.indexRows = await window.SearchIndexLoader.getIndex();
      this.renderGrid();
      this.updateCacheStatus(`Loaded ${this.products.length} products`, true);
    } catch (error) {
      console.error('Error initializing search grid:', error);
      this.updateCacheStatus('Error loading search results', false, true);
      throw error;
    }
  }

  /**
   * Compute the filtered/sorted/pinned result set from the index.
   * Overrides the base class so the inherited renderGrid/pagination and the
   * inherited filter/sort event listeners all reflect the current query+filters
   * with no network calls. Memoized by filter signature (page changes reuse it).
   */
  getFilteredProducts() {
    if (!this.indexRows) return this.products || [];

    const key = JSON.stringify({ q: this.searchTerms, f: this.filters });
    if (key !== this._resultsKey) {
      this._resultsKey = key;
      this._results = window.SearchMatching.applySearch(
        this.indexRows,
        this.searchTerms,
        this.filters
      );
    }
    // Keep this.products in sync for the debug panel / status messages.
    this.products = this._results;
    return this._results;
  }

  renderProductList(products) {
    const template = document.getElementById('product-item-template');
    const fragment = document.createDocumentFragment();

    products.forEach((product) => {
      const clone = template.content.cloneNode(true);
      const listItem = clone.querySelector('.product-item');

      listItem.setAttribute('data-product-id', product.id);
      clone.querySelector('.sku-value').textContent = product.sku || 'No SKU';

      const priceElement = clone.querySelector('.product-price');
      if (product.showPrice === false) {
        priceElement.textContent = '';
        priceElement.style.display = 'none';
      } else if (product.available && product.price) {
        priceElement.textContent = `$${parseFloat(product.price).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
      } else {
        priceElement.textContent = '';
      }

      const justArrivedElement = clone.querySelector('.product-just-arrived');
      if (product.justArrived === true || product.justArrived === 'true') {
        justArrivedElement.style.display = 'block';
      }

      // Availability first — sold always wins over hold, so a sold-out item is
      // never badged "On Hold" even if the index says so.
      listItem.classList.add(product.available ? 'available' : 'sold-out');

      // Handle onHold status (only meaningful while the item is still in stock)
      if (product.onHold && product.available) {
        listItem.classList.add('on-hold');
        const priceEl = clone.querySelector('.product-price');
        if (priceEl && product.showPrice !== false) {
          priceEl.textContent = 'On Hold';
          priceEl.style.display = '';
        }
      }

      fragment.appendChild(clone);
    });

    // Hide loading message and show products
    const loadingMessage = document.getElementById('loading-message');
    if (loadingMessage) loadingMessage.style.display = 'none';

    this.productList.innerHTML = '';
    this.productList.appendChild(fragment);

    // Populate the remaining display data (links, images, titles, promos)
    this.fetchDisplayData(products);
  }

  async fetchDisplayData(products) {
    products.forEach(product => {
      this.updateSingleProductDisplay({
        id: product.id,
        title: product.title,
        handle: product.handle,
        featured_image: product.image,
        justArrived: product.justArrived,
        promoHeadline: product.promoHeadline,
        onHold: product.onHold,
        available: product.available,
        showPrice: product.showPrice,
      });
    });
  }

  updateSingleProductDisplay(displayData) {
    const listItem = this.productList.querySelector(`li[data-product-id="${displayData.id}"]`);
    if (listItem) {
      const linkElement = listItem.querySelector('.product-link');
      const imageElement = listItem.querySelector('.product-image-placeholder');
      const justArrivedElement = listItem.querySelector('.product-just-arrived');
      const promoHeadlineElement = listItem.querySelector('.product-promo-headline');
      const productNameElement = listItem.querySelector('.product-name');

      if (linkElement) {
        linkElement.href = `/products/${displayData.handle}`;
        linkElement.classList.remove('loading');
      }
      if (productNameElement) {
        productNameElement.textContent = displayData.title;
      }
      if (imageElement) {
        imageElement.addEventListener('error', () => this.recoverThumbnail(imageElement, displayData.handle), {
          once: true,
        });
        imageElement.src = displayData.featured_image || window.PRODUCT_PLACEHOLDER_IMAGE || '';
        imageElement.alt = displayData.title;
        imageElement.style.display = 'block';
      }
      if (justArrivedElement && displayData.justArrived) {
        justArrivedElement.style.display = 'block';
      }
      if (promoHeadlineElement && displayData.promoHeadline) {
        promoHeadlineElement.textContent = displayData.promoHeadline;
        promoHeadlineElement.style.display = 'block';
      }
      // Never badge a sold item as on-hold — the "Sold" badge must stand.
      if (displayData.onHold && displayData.available) {
        listItem.classList.add('on-hold');
      }
    }
  }

  /**
   * A result's thumbnail 404s when the product was re-imported after the index
   * was built: each import run deletes and recreates recently changed products,
   * which gives them new photo URLs. Ask Shopify for the current photo, and show
   * the placeholder only if that fails too.
   */
  async recoverThumbnail(image, handle) {
    const placeholder = window.PRODUCT_PLACEHOLDER_IMAGE || '';
    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
      const product = response.ok ? await response.json() : null;
      if (product?.featured_image) {
        image.addEventListener('error', () => { image.src = placeholder; }, { once: true });
        image.src = product.featured_image;
        return;
      }
    } catch (error) {
      console.warn(`Search grid: couldn't fetch the current photo for ${handle}:`, error);
    }
    image.src = placeholder;
  }

  populateFilterOptions() {
    const predefinedMakers = [
      'Fender', 'Martin', 'Gibson', 'D\'Angelico', 'Gilchrist',
      'Collings', 'Rickenbacker', 'Gretsch', 'Epiphone', 'National',
      'Guild', 'Harmony', 'Burns', 'Vox', 'Danelectro', 'Vega', 'Other'
    ];

    const predefinedEras = [
      '2000s', '1990s', '1980s', '1970s', '1960s',
      '1950s', '1940s', '1930s', '1920s', '1910s', 'pre 1910'
    ];

    if (this.makerFilter) {
      this.makerFilter.innerHTML = '<option value="">Any maker</option>' +
        predefinedMakers.map(maker => `<option value="${maker}">${maker}</option>`).join('');
    }

    if (this.eraFilter) {
      this.eraFilter.innerHTML = '<option value="">Any era</option>' +
        predefinedEras.map(era => `<option value="${era}">${era}</option>`).join('');
    }
  }

  updateURL(pushState = true) {
    const urlParams = new URLSearchParams();

    if (this.searchTerms) {
      urlParams.set('q', this.searchTerms);
    }
    if (this.filters.includeSold) {
      urlParams.set('include_sold', 'true');
    }
    if (this.filters.maker) {
      urlParams.set('maker', this.filters.maker);
    }
    if (this.filters.era) {
      urlParams.set('era', this.filters.era);
    }
    if (this.filters.sort && this.filters.sort !== 'recent') {
      urlParams.set('sort', this.filters.sort);
    }
    if (this.currentPage > 1) {
      urlParams.set('page', this.currentPage.toString());
    }

    const currentParams = new URLSearchParams(window.location.search);
    if (currentParams.get('debug')) {
      urlParams.set('debug', currentParams.get('debug'));
    }

    const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');

    if (pushState) {
      const state = {
        gridFilters: {
          searchTerms: this.searchTerms,
          includeSold: this.filters.includeSold,
          maker: this.filters.maker,
          era: this.filters.era,
          sort: this.filters.sort,
          page: this.currentPage
        }
      };
      window.history.pushState(state, '', newUrl);
    }
  }

  restoreStateFromURL(updateURL = true) {
    const urlParams = new URLSearchParams(window.location.search);

    this.filters.includeSold = urlParams.get('include_sold') === 'true';
    this.filters.maker = urlParams.get('maker') || '';
    this.filters.era = urlParams.get('era') || '';
    this.filters.sort = urlParams.get('sort') || 'recent';
    this.currentPage = parseInt(urlParams.get('page')) || 1;

    if (this.includeSoldCheckbox) this.includeSoldCheckbox.checked = this.filters.includeSold;
    if (this.makerFilter) this.makerFilter.value = this.filters.maker;
    if (this.eraFilter) this.eraFilter.value = this.filters.era;
    if (this.sortFilter) this.sortFilter.value = this.filters.sort;

    if (updateURL) {
      this.updateURL(false);
    }
  }

  clearCache() {
    this._resultsKey = null;
    this._results = [];
  }
}

// Static initialization method
SearchGridController.initialize = async function(options) {
  if (window.SearchGridController && typeof window.SearchGridController.products !== 'undefined') {
    console.log('Search grid controller already exists, skipping initialization');
    return window.SearchGridController;
  }

  const controller = new SearchGridController(options);
  await controller.initialize();

  window.SearchGridController = controller;
  return controller;
};

// Export for use
window.SearchGridController = SearchGridController;
